// =============================================================================
// SageJournalTab — Real-Estate purchase/sale → Sage Intacct Journal Entry.
//
// The platform figures out which uploaded documents are settlement statements
// (HUD-1 / closing statements) on its own — no document selection required.
// On open, this tab asks the backend to auto-detect settlement statements for
// the detail and prepare a draft JE for each (purchase price + closing costs
// capitalized to the land account, earnest money credited, net cash credited
// to the clearing account, balanced). The user reviews/edits header + lines
// and exports the exact 28-column Sage Intacct GL-import CSV.
//
// A collapsed "Generate from a specific document" override remains for the rare
// case the auto-detector misses a non-standard statement.
//
// Before finalizing/exporting we REQUIRE ≥1 parcel and a purchase (settlement)
// date; the entry date is stamped as the detail's PurchaseDate on save.
//
// The 28-column CSV layout mirrors the canonical builder in
// api/src/functions/realEstateJournal.js (toIntacctCsv) — keep the two in sync.
// =============================================================================
import { useState, useEffect, useCallback } from 'react';
import { B } from '../../constants/brand';
import { fmt } from '../../utils/format';
import { api } from '../../api/client';
import { Btn, Badge, Spinner, ErrorMsg, useToast } from '../ui';
import { DocViewerModal } from './DocViewerModal';

const CSV_COLUMNS = [
  'DONOTIMPORT', 'JOURNAL', 'DATE', 'REVERSEDATE', 'DESCRIPTION', 'REFERENCE_NO',
  'LINE_NO', 'ACCT_NO', 'LOCATION_ID', 'DEPT_ID', 'DOCUMENT', 'MEMO', 'DEBIT',
  'CREDIT', 'SOURCEENTITY', 'CURRENCY', 'EXCH_RATE_DATE', 'EXCH_RATE_TYPE_ID',
  'EXCHANGE_RATE', 'STATE', 'ALLOCATION_ID', 'BILLABLE', 'RPESENTRY',
  'GLDIMPARCEL', 'GLENTRY_CUSTOMERID', 'GLENTRY_VENDORID', 'GLENTRY_EMPLOYEEID',
  'GLENTRY_ITEMID', 'GLENTRY_CLASSID',
];

const csvEscape = (v) => {
  const s = v == null ? '' : String(v);
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};
const mdY = (d) => {
  if (!d) return '';
  const s = String(d);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${+m[2]}/${+m[3]}/${m[1]}`;
  const dt = new Date(s);
  return Number.isNaN(dt.getTime()) ? '' : `${dt.getUTCMonth() + 1}/${dt.getUTCDate()}/${dt.getUTCFullYear()}`;
};
const money = (n) => {
  const v = parseFloat(n);
  return Number.isFinite(v) && v !== 0 ? v.toFixed(2) : '';
};
const round2 = (n) => Math.round((parseFloat(n) || 0) * 100) / 100;

// Build the Intacct CSV text from the working entry + its (included) lines.
function buildCsv(entry, lines) {
  const included = lines.filter(l => l.isIncluded);
  const rowFor = (l, i) => {
    const map = {
      DONOTIMPORT: '', JOURNAL: entry.journal || '', DATE: mdY(entry.entryDate),
      REVERSEDATE: mdY(entry.reverseDate), DESCRIPTION: entry.description || '',
      REFERENCE_NO: entry.referenceNo || '', LINE_NO: i + 1, ACCT_NO: l.acctNo || '',
      LOCATION_ID: l.locationID || entry.locationID || '', DEPT_ID: l.deptID || entry.deptID || '',
      DOCUMENT: '', MEMO: l.memo || '', DEBIT: money(l.debit), CREDIT: money(l.credit),
      SOURCEENTITY: entry.sourceEntity || '', CURRENCY: '', EXCH_RATE_DATE: '',
      EXCH_RATE_TYPE_ID: '', EXCHANGE_RATE: '', STATE: entry.journalState || '',
      ALLOCATION_ID: '', BILLABLE: '', RPESENTRY: '', GLDIMPARCEL: '',
      GLENTRY_CUSTOMERID: '', GLENTRY_VENDORID: '', GLENTRY_EMPLOYEEID: '',
      GLENTRY_ITEMID: '', GLENTRY_CLASSID: '',
    };
    return CSV_COLUMNS.map(c => csvEscape(map[c])).join(',');
  };
  return [CSV_COLUMNS.join(','), ...included.map(rowFor)].join('\r\n');
}

const STATE_OPTIONS = [{ value: 'Draft', label: 'Draft' }, { value: 'Posted', label: 'Posted' }];

// Fold a freshly-generated full JE into the summary list (replace or prepend).
function upsertEntry(list, created) {
  const row = {
    journalEntryID: created.journalEntryID, entryType: created.entryType, journal: created.journal,
    entryDate: created.entryDate, description: created.description, referenceNo: created.referenceNo,
    journalState: created.journalState, status: created.status, contractPrice: created.contractPrice,
    earnestMoney: created.earnestMoney, netAmount: created.netAmount, sellerName: created.sellerName,
    fileNumber: created.fileNumber, documentID: created.documentID,
    createdUTC: created.createdUTC, modifiedUTC: created.modifiedUTC,
  };
  const idx = list.findIndex(e => e.journalEntryID === row.journalEntryID);
  if (idx >= 0) { const copy = list.slice(); copy[idx] = row; return copy; }
  return [row, ...list];
}

export const SageJournalTab = ({ dealId, dealDetailId }) => {
  const { toast } = useToast();
  const [loading, setLoading]   = useState(true);
  const [scanning, setScanning] = useState(false);
  const [error, setError]       = useState(null);
  const [entries, setEntries]   = useState([]);
  const [requirements, setReq]  = useState({ parcelCount: 0, hasPurchaseDate: false });
  const [indexingPending, setIndexingPending] = useState(0);
  const [duplicates, setDuplicates] = useState(0);   // counterparty copies of the same closing, skipped
  const [preparing, setPreparing] = useState(null); // { done, total } while generating drafts
  const [docs, setDocs]         = useState([]);
  const [sageEntities, setSageEntities] = useState([]); // imported Sage source-entity list

  // Advanced (manual override) state
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [pickDoc, setPickDoc]   = useState('');
  const [pickType, setPickType] = useState('Purchase');
  const [generating, setGenerating] = useState(false);

  // Open editor state
  const [openEntry, setOpenEntry] = useState(null);
  const [lines, setLines]         = useState([]);
  const [saving, setSaving]       = useState(false);
  const [viewerState, setViewerState] = useState(null); // { doc, sasUrl } for DocViewerModal
  const [viewingStmt, setViewingStmt] = useState(null);  // journalEntryID being opened

  // Detect settlement statements (fast, no AI), then prepare each one in its own
  // request so no single call blocks on several Claude parses (the cause of the
  // old 10s timeout). Each /generate is one parse and gets a generous timeout.
  const scan = useCallback(async (isRescan) => {
    setScanning(true); setError(null);
    try {
      const [auto, docList, entityList] = await Promise.all([
        api.post(`deals/${dealId}/details/${dealDetailId}/journal-entries/auto`, {}),
        api.get(`documents?dealDetailId=${dealDetailId}`),
        api.get('sage/entities').catch(() => []),
      ]);
      setEntries(auto.entries || []);
      setReq(auto.requirements || { parcelCount: 0, hasPurchaseDate: false });
      setIndexingPending(auto.indexingPending || 0);
      setDuplicates(auto.duplicates || 0);
      setDocs(docList || []);
      // GET /sage/entities answers { entities } since the Company Entities
      // merge (migration 189); the bare array is the pre-merge shape and is
      // still accepted so a browser holding a stale bundle degrades to a
      // working dropdown rather than an empty one.
      setSageEntities(Array.isArray(entityList) ? entityList : (entityList?.entities || []));
      setLoading(false); // render the tab now; prepare drafts incrementally below

      const todo = auto.detected || [];
      if (todo.length === 0) {
        if (isRescan) toast.info?.('No new settlement statements detected.');
        return;
      }
      setPreparing({ done: 0, total: todo.length });
      let prepared = 0;
      for (let i = 0; i < todo.length; i++) {
        const d = todo[i];
        try {
          const created = await api.post(
            `deals/${dealId}/details/${dealDetailId}/journal-entries/generate`,
            { documentId: d.documentID, entryType: d.entryType },
            { timeout: 45000 },
          );
          prepared++;
          setEntries(prev => upsertEntry(prev, created));
          if (created.requirements) setReq(created.requirements);
        } catch (e) {
          toast.error(`Couldn't prepare "${d.fileName}": ${e.message}`);
        }
        setPreparing({ done: i + 1, total: todo.length });
      }
      setPreparing(null);
      if (prepared > 0) toast.success(`Prepared ${prepared} settlement statement${prepared > 1 ? 's' : ''}.`);
    } catch (e) { setError(e.message); }
    finally { setScanning(false); setLoading(false); setPreparing(null); }
  }, [dealId, dealDetailId, toast]);

  useEffect(() => { scan(false); }, [scan]);

  const openFull = async (jeId) => {
    try { hydrate(await api.get(`journal-entries/${jeId}`)); }
    catch (e) { toast.error(e.message); }
  };

  // Open ONLY the settlement-statement pages of the source document.
  const viewStatement = async (jeId) => {
    setViewingStmt(jeId);
    try {
      const r = await api.get(`journal-entries/${jeId}/statement-view`, { timeout: 30000 });
      setViewerState({ doc: { fileName: r.fileName || 'Settlement Statement.pdf' }, sasUrl: r.sasUrl });
    } catch (e) { toast.error(e.message || 'Could not open the settlement statement'); }
    finally { setViewingStmt(null); }
  };

  const hydrate = (full) => {
    setOpenEntry(full);
    setReq(full.requirements || requirements);
    setLines((full.lines || []).map(l => ({
      lineID: l.lineID, acctNo: l.acctNo || '', memo: l.memo || '',
      debit: l.debit ? String(l.debit) : '', credit: l.credit ? String(l.credit) : '',
      locationID: l.locationID || '', deptID: l.deptID || '',
      isIncluded: l.isIncluded !== false, lineKey: l.lineKey || null,
    })));
  };

  // Manual override — generate from a specific document the auto-detector missed.
  const generateManual = async () => {
    if (!pickDoc) { toast.error('Choose a document first.'); return; }
    setGenerating(true);
    try {
      const created = await api.post(`deals/${dealId}/details/${dealDetailId}/journal-entries/generate`, {
        documentId: pickDoc, entryType: pickType,
      });
      hydrate(created);
      await scan(false);
      toast.success('Journal entry prepared. Review the lines below.');
    } catch (e) { toast.error(e.message || 'Generation failed'); }
    finally { setGenerating(false); }
  };

  const setHeader = (k, v) => setOpenEntry(e => ({ ...e, [k]: v }));
  const setLine = (i, k, v) => setLines(ls => ls.map((l, idx) => idx === i ? { ...l, [k]: v } : l));
  const addLine = () => setLines(ls => [...ls, { acctNo: openEntry.landAcctNo || '', memo: '', debit: '', credit: '', isIncluded: true }]);
  const removeLine = (i) => setLines(ls => ls.filter((_, idx) => idx !== i));

  const totDebit  = round2(lines.filter(l => l.isIncluded).reduce((s, l) => s + (parseFloat(l.debit) || 0), 0));
  const totCredit = round2(lines.filter(l => l.isIncluded).reduce((s, l) => s + (parseFloat(l.credit) || 0), 0));
  const balanced  = Math.abs(totDebit - totCredit) < 0.005;

  const save = async () => {
    setSaving(true);
    try {
      const updated = await api.put(`journal-entries/${openEntry.journalEntryID}`, {
        journal: openEntry.journal, entryDate: openEntry.entryDate || null,
        reverseDate: openEntry.reverseDate || null, description: openEntry.description,
        referenceNo: openEntry.referenceNo || null, journalState: openEntry.journalState,
        locationId: openEntry.locationID, deptId: openEntry.deptID, sourceEntity: openEntry.sourceEntity,
        landAcctNo: openEntry.landAcctNo, clearingAcctNo: openEntry.clearingAcctNo,
        entryType: openEntry.entryType,
        lines: lines.map((l, i) => ({
          lineNo: i + 1, acctNo: l.acctNo, memo: l.memo,
          debit: l.debit === '' ? null : parseFloat(l.debit),
          credit: l.credit === '' ? null : parseFloat(l.credit),
          locationId: l.locationID || null, deptId: l.deptID || null,
          isIncluded: l.isIncluded, lineKey: l.lineKey || null,
        })),
      });
      hydrate(updated);
      await scan(false);
      toast.success('Saved.');
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  // The server refuses an entry that does not balance, and one whose clearing
  // line does not match the statement's cash to close (a misread line hidden
  // in the plug). The second can be overridden by someone who has checked it.
  const finalize = async (overrideCashCheck = false) => {
    try {
      const updated = await api.post(`journal-entries/${openEntry.journalEntryID}/finalize`, overrideCashCheck ? { overrideCashCheck: true } : {});
      hydrate(updated);
      await scan(false);
      toast.success('Journal entry finalized.');
    } catch (e) {
      if (!overrideCashCheck && e.body?.code === 'CASH_MISMATCH') {
        if (window.confirm(`${e.body.error}\n\nFinalize anyway? Only do this if you have checked the statement.`)) finalize(true);
        return;
      }
      toast.error(e.message || 'Could not finalize');
    }
  };

  const removeEntry = async (jeId) => {
    if (!window.confirm('Delete this journal entry?')) return;
    try {
      await api.del(`journal-entries/${jeId}`);
      if (openEntry?.journalEntryID === jeId) { setOpenEntry(null); setLines([]); }
      await scan(false);
    } catch (e) { toast.error(e.message); }
  };

  const exportCsv = () => {
    const gateMsg = gate();
    if (gateMsg) { toast.error(gateMsg); return; }
    const csv = buildCsv(openEntry, lines);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const safe = (openEntry.sellerName || openEntry.fileNumber || 'entry').replace(/[^A-Za-z0-9_-]+/g, '_').slice(0, 40);
    a.download = `JE_${openEntry.journal || 'SJ'}_${mdY(openEntry.entryDate).replace(/\//g, '-') || 'nodate'}_${safe}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Returns a blocking message, or null when export/finalize is allowed.
  const gate = () => {
    if (!balanced) return 'The entry must balance (debits = credits) before export.';
    if (requirements.parcelCount === 0) return 'Add at least one parcel to this deal detail (Parcels tab) first.';
    if (!openEntry?.entryDate) return 'Set the purchase (settlement) date first.';
    return null;
  };

  if (loading) return <Spinner label="Detecting settlement statements…" />;
  if (error)   return <ErrorMsg msg={error} onRetry={() => scan(false)} />;

  const labeled = (label, node) => (<div><div style={lblStyle}>{label}</div>{node}</div>);

  return (
    <div style={{ marginTop: 8 }}>
      {/* Cash tie-out: the plug line makes any extraction balance, so the
          server compares it with the statement's cash to close. */}
      {openEntry?.checks?.tiesToCash === false && (
        <div style={gateStyle}>
          <strong>Check this entry against the statement:</strong>{' '}
          {(openEntry.checks.issues || []).find(i => i.code === 'CASH_MISMATCH')?.message}
        </div>
      )}
      {/* Requirements gate banner */}
      {(requirements.parcelCount === 0 || !requirements.hasPurchaseDate) && (
        <div style={gateStyle}>
          <strong>Before a JE can be finalized/exported:</strong>
          <ul style={{ margin: '6px 0 0 18px', padding: 0 }}>
            {requirements.parcelCount === 0 && <li>Add at least one <b>parcel</b> on the <b>Parcels</b> tab.</li>}
            {!requirements.hasPurchaseDate && <li>Set the <b>purchase (settlement) date</b> below and save — it is stamped on the detail and its parcels.</li>}
          </ul>
        </div>
      )}

      {/* Auto-detection status */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, gap: 12, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 13, color: B.textMd }}>
          {preparing
            ? `Preparing ${preparing.done} of ${preparing.total} detected settlement statement${preparing.total > 1 ? 's' : ''}…`
            : scanning
              ? 'Scanning documents for settlement statements…'
              : entries.length > 0
                ? `${entries.length} journal ${entries.length === 1 ? 'entry' : 'entries'} prepared from detected settlement statements.`
                : indexingPending > 0
                  ? `${indexingPending} settlement statement${indexingPending > 1 ? 's are' : ' is'} still being read — re-scan once indexing finishes and the JE will be prepared automatically.`
                  : 'No settlement statements detected yet. Upload a signed settlement statement (HUD-1) on the Documents tab — it will be detected and a Sage JE prepared automatically.'}
          {/* A statement can still be indexing while an earlier closing already
              has a JE. The primary message reports the entries, so the pending
              one is appended rather than lost to the ternary above. */}
          {!preparing && !scanning && entries.length > 0 && indexingPending > 0 && (
            <span style={{ color: B.textLt }}> {' '}({indexingPending} more still being read.)</span>
          )}
          {duplicates > 0 && (
            <span style={{ color: B.textLt }}> {' '}({duplicates} duplicate cop{duplicates > 1 ? 'ies' : 'y'} of the same closing skipped.)</span>
          )}
        </div>
        <Btn size="sm" variant="secondary" onClick={() => scan(true)} loading={scanning}>Re-scan</Btn>
      </div>

      {/* Existing entries */}
      {entries.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          {entries.map(e => (
            <div key={e.journalEntryID} style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '8px 10px', borderBottom: `1px solid ${B.border}`,
              background: openEntry?.journalEntryID === e.journalEntryID ? B.tealLt : 'transparent',
            }}>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center', minWidth: 0 }}>
                <Badge color={e.status === 'Final' ? B.green : B.amber}>{e.status}</Badge>
                <span style={{ fontSize: 13, color: B.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.description}</span>
                <span style={{ fontSize: 12, color: B.textLt }}>{fmt.date(e.entryDate)}</span>
                <span style={{ fontSize: 13, color: B.navy }}>{fmt.currency(e.netAmount)}</span>
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                {e.documentID && (
                  <Btn size="sm" variant="secondary" onClick={() => viewStatement(e.journalEntryID)} loading={viewingStmt === e.journalEntryID}>Statement</Btn>
                )}
                <Btn size="sm" variant="secondary" onClick={() => openFull(e.journalEntryID)}>Open</Btn>
                <Btn size="sm" variant="danger" onClick={() => removeEntry(e.journalEntryID)}>Delete</Btn>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Advanced: manual override */}
      <div style={{ marginBottom: 16 }}>
        <button onClick={() => setShowAdvanced(s => !s)} style={linkBtn}>
          {showAdvanced ? '▾' : '▸'} Generate from a specific document
        </button>
        {showAdvanced && (
          <div style={{ border: `1px solid ${B.border}`, borderRadius: 10, padding: 14, marginTop: 8, background: B.surface }}>
            <div style={{ fontSize: 12, color: B.textLt, marginBottom: 10 }}>
              Use this only if a statement wasn’t auto-detected. Otherwise just upload the document — it’s detected automatically.
            </div>
            <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
              <div style={{ minWidth: 280, flex: 1 }}>
                {labeled('Document', (
                  <select value={pickDoc} onChange={e => setPickDoc(e.target.value)} style={selStyle}>
                    <option value="">— Select a document —</option>
                    {docs.map(d => (
                      <option key={d.documentID} value={d.documentID} disabled={!d.isOCRComplete}>
                        {d.fileName}{d.docCategory ? ` · ${d.docCategory}` : ''}{d.isOCRComplete ? '' : ' (indexing…)'}
                      </option>
                    ))}
                  </select>
                ))}
              </div>
              <div style={{ width: 140 }}>
                {labeled('Type', (
                  <select value={pickType} onChange={e => setPickType(e.target.value)} style={selStyle}>
                    <option value="Purchase">Purchase</option>
                    <option value="Sale">Sale</option>
                  </select>
                ))}
              </div>
              <Btn onClick={generateManual} loading={generating} disabled={!pickDoc}>Generate</Btn>
            </div>
          </div>
        )}
      </div>

      {/* Editor */}
      {openEntry && (
        <div style={{ border: `1px solid ${B.border}`, borderRadius: 10, padding: 16, background: B.surface }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, gap: 10 }}>
            <div style={{ fontWeight: 700, fontSize: 15, color: B.text, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{openEntry.description}</div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 }}>
              {openEntry.documentID && (
                <Btn size="sm" variant="secondary" onClick={() => viewStatement(openEntry.journalEntryID)} loading={viewingStmt === openEntry.journalEntryID}>View settlement statement</Btn>
              )}
              <Badge color={openEntry.status === 'Final' ? B.green : B.amber}>{openEntry.status}</Badge>
            </div>
          </div>

          {/* Header grid */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 14 }}>
            {labeled('Journal', <input value={openEntry.journal || ''} onChange={e => setHeader('journal', e.target.value)} style={inpStyle} />)}
            {labeled('Purchase / settlement date', <input type="date" value={(openEntry.entryDate || '').slice(0, 10)} onChange={e => setHeader('entryDate', e.target.value)} style={inpStyle} />)}
            {labeled('Reference #', <input value={openEntry.referenceNo || ''} onChange={e => setHeader('referenceNo', e.target.value)} style={inpStyle} />)}
            {labeled('State', (
              <select value={openEntry.journalState || 'Draft'} onChange={e => setHeader('journalState', e.target.value)} style={selStyle}>
                {STATE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            ))}
            {labeled('Location (from deal’s Sage location)', (
              <div style={readonlyBox} title="Set by the deal detail's Sage Location">
                {openEntry.locationID || <span style={{ color: B.textLt }}>— no Sage location on this detail —</span>}
              </div>
            ))}
            {labeled('Department', <input value={openEntry.deptID || ''} onChange={e => setHeader('deptID', e.target.value)} style={inpStyle} />)}
            {labeled('Source entity', (
              <select value={openEntry.sourceEntity || ''} onChange={e => setHeader('sourceEntity', e.target.value)} style={selStyle}>
                <option value="">— Select entity —</option>
                {sageEntities.map(en => (
                  <option key={en.sageEntityID || en.entityCode} value={en.entityCode}>{en.name ? `${en.entityCode} — ${en.name}` : en.entityCode}</option>
                ))}
                {openEntry.sourceEntity && !sageEntities.some(en => en.entityCode === openEntry.sourceEntity) && (
                  <option value={openEntry.sourceEntity}>{openEntry.sourceEntity} (not in Sage list)</option>
                )}
              </select>
            ))}
            {labeled('Description', <input value={openEntry.description || ''} onChange={e => setHeader('description', e.target.value)} style={inpStyle} />)}
          </div>

          {/* Lines */}
          <div style={{ overflowX: 'auto', border: `1px solid ${B.border}`, borderRadius: 8 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: B.surfaceMuted }}>
                  {['#', 'Incl', 'Account', 'Memo', 'Debit', 'Credit', ''].map((h, i) => (
                    <th key={i} style={{ ...thStyle, textAlign: i >= 4 && i <= 5 ? 'right' : 'left' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {lines.map((l, i) => (
                  <tr key={l.lineID || i} style={{ borderBottom: `1px solid ${B.divider}`, opacity: l.isIncluded ? 1 : 0.5 }}>
                    <td style={tdStyle}>{i + 1}</td>
                    <td style={{ ...tdStyle, textAlign: 'center' }}>
                      <input type="checkbox" checked={l.isIncluded} onChange={e => setLine(i, 'isIncluded', e.target.checked)} />
                    </td>
                    <td style={tdStyle}><input value={l.acctNo} onChange={e => setLine(i, 'acctNo', e.target.value)} style={{ ...cellInp, width: 80 }} /></td>
                    <td style={tdStyle}><input value={l.memo} onChange={e => setLine(i, 'memo', e.target.value)} style={{ ...cellInp, width: '100%', minWidth: 200 }} /></td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}><input value={l.debit} onChange={e => setLine(i, 'debit', e.target.value)} inputMode="decimal" style={{ ...cellInp, width: 110, textAlign: 'right' }} /></td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}><input value={l.credit} onChange={e => setLine(i, 'credit', e.target.value)} inputMode="decimal" style={{ ...cellInp, width: 110, textAlign: 'right' }} /></td>
                    <td style={{ ...tdStyle, textAlign: 'center' }}><button onClick={() => removeLine(i)} style={xBtn} title="Remove line">×</button></td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ background: B.surfaceMuted, fontWeight: 600 }}>
                  <td style={tdStyle} colSpan={4}>
                    <span style={{ color: balanced ? B.green : B.red }}>
                      {balanced ? 'Balanced ✓' : `Out of balance by ${fmt.currency(Math.abs(totDebit - totCredit))}`}
                    </span>
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'right' }}>{fmt.currency(totDebit)}</td>
                  <td style={{ ...tdStyle, textAlign: 'right' }}>{fmt.currency(totCredit)}</td>
                  <td style={tdStyle}></td>
                </tr>
              </tfoot>
            </table>
          </div>

          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 12, flexWrap: 'wrap' }}>
            <Btn size="sm" variant="secondary" onClick={addLine}>+ Add line</Btn>
            <div style={{ flex: 1 }} />
            <Btn size="sm" variant="secondary" onClick={() => { setOpenEntry(null); setLines([]); }}>Close</Btn>
            <Btn size="sm" onClick={save} loading={saving}>Save</Btn>
            <Btn size="sm" variant="secondary" onClick={exportCsv} disabled={!!gate()} title={gate() || 'Download Sage Intacct CSV'}>Export CSV</Btn>
            {openEntry.status !== 'Final' && (
              <Btn size="sm" onClick={() => finalize(false)} disabled={!!gate()} title={gate() || 'Mark final + stamp purchase date'}>Finalize</Btn>
            )}
          </div>
          {gate() && <div style={{ color: B.amber, fontSize: 12, marginTop: 8 }}>{gate()}</div>}
        </div>
      )}

      <DocViewerModal viewerState={viewerState} onClose={() => setViewerState(null)} />
    </div>
  );
};

// ── inline styles ──
const lblStyle = { fontSize: 11, color: B.textMd, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 3 };
const inpStyle = { width: '100%', padding: '7px 9px', border: `1px solid ${B.border}`, borderRadius: 6, fontSize: 13, boxSizing: 'border-box' };
const selStyle = { ...inpStyle, background: B.surface, cursor: 'pointer' };
const readonlyBox = { ...inpStyle, background: B.surfaceMuted, color: B.textMd, display: 'flex', alignItems: 'center', minHeight: 33 };
const cellInp  = { padding: '5px 7px', border: `1px solid ${B.border}`, borderRadius: 5, fontSize: 13, boxSizing: 'border-box' };
const thStyle  = { padding: '8px 10px', fontSize: 11, color: B.textMd, textTransform: 'uppercase', letterSpacing: 0.4, borderBottom: `1px solid ${B.border}`, whiteSpace: 'nowrap' };
const tdStyle  = { padding: '5px 8px', color: B.text };
const xBtn     = { border: 'none', background: 'transparent', color: B.red, cursor: 'pointer', fontSize: 16, lineHeight: 1 };
const gateStyle = { background: B.amber + '14', border: `1px solid ${B.amber}55`, borderRadius: 8, padding: '10px 14px', fontSize: 13, color: B.textMd, marginBottom: 14 };
const linkBtn  = { border: 'none', background: 'transparent', color: B.navy, cursor: 'pointer', fontSize: 12, fontWeight: 600, padding: 0 };
